import { Button, Flex, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AdminAccess,
  AdminAccessListEnum,
  AdminAccessStatusEnum,
  useAcceptAdminAccessMutation,
  useDeclineAdminAccessMutation,
  useGetAllAdminAccessQuery
} from 'api/AdminAccessApi';
import { ResponsiveTable } from 'components/UiKit/ResponsiveTable/ResponsiveTable';
import { useState } from 'react';
import { getAdminAccessDisplayName, logging } from 'utils/utils';
import './BeneficiaryInviteListComponent.css';

const PAGE_SIZE = 10;

const getAdminAccessEmail = (adminAccess: AdminAccess) => adminAccess.beneficiaryEmail || adminAccess.email || '—';

function BeneficiaryInviteListComponent() {
  const [currentPage, setCurrentPage] = useState(1);

  const { data: beneficiariesData, isFetching } = useGetAllAdminAccessQuery({
    list: AdminAccessListEnum.Users,
    limit: PAGE_SIZE,
    filter: { status: AdminAccessStatusEnum.Invited },
    offset: (currentPage - 1) * PAGE_SIZE
  });

  const [acceptAdminAccess, { isLoading: isLoadingAccept }] = useAcceptAdminAccessMutation();
  const [declineAdminAccess, { isLoading: isLoadingDecline }] = useDeclineAdminAccessMutation();

  const handleAccept = async (id: string) => {
    try {
      await acceptAdminAccess({ id }).unwrap();
    } catch (e: any) {
      logging(e);
    }
  };

  const handleDecline = async (id: string) => {
    try {
      await declineAdminAccess({ id }).unwrap();
    } catch (e: any) {
      logging(e);
    }
  };

  const onTableChanged = (pagination, _filters, _sorter) => {
    setCurrentPage(pagination.current);
  };

  const renderActions = (adminAccess: AdminAccess) => {
    return (
      <Space size="small">
        <Button type="primary" size="small" onClick={() => handleAccept(adminAccess.id)} loading={isLoadingAccept}>
          Accept
        </Button>
        <Button size="small" onClick={() => handleDecline(adminAccess.id)} loading={isLoadingDecline}>
          Decline
        </Button>
      </Space>
    );
  };

  const mobileColumns: ColumnsType<AdminAccess> = [
    {
      render: (adminAccess: AdminAccess) => (
        <Space size={10} direction="vertical" className="responsive-table-card">
          <div>
            <div className="responsive-table-title">{getAdminAccessDisplayName(adminAccess, '—')}</div>
            <div>{getAdminAccessEmail(adminAccess)}</div>
          </div>

          <Flex justify="space-between">
            <div />
            <div>{renderActions(adminAccess)}</div>
          </Flex>
        </Space>
      )
    }
  ];

  const columns: ColumnsType<AdminAccess> = [
    {
      title: 'Beneficiary Name',
      key: 'name',
      dataIndex: 'displayName',
      ellipsis: true,
      render: (_, adminAccess) => getAdminAccessDisplayName(adminAccess, '—')
    },
    {
      title: 'Beneficiary Email',
      key: 'email',
      dataIndex: 'email',
      ellipsis: true,
      render: (_, adminAccess) => getAdminAccessEmail(adminAccess)
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, adminAccess) => renderActions(adminAccess)
    }
  ];

  if (!beneficiariesData?.total) {
    return null;
  }

  return (
    <div className="beneficiary-list-component">
      <h3>Pending Acceptance ({beneficiariesData?.total || 0})</h3>

      <ResponsiveTable
        mobileUntilBreakpoint="lg"
        mobileColumns={mobileColumns}
        columns={columns}
        bordered
        rowKey="id"
        onChange={onTableChanged}
        dataSource={beneficiariesData?.list || []}
        pagination={{
          defaultPageSize: PAGE_SIZE,
          current: currentPage,
          total: beneficiariesData?.total || 0
        }}
        loading={isFetching}
      />
    </div>
  );
}

export default BeneficiaryInviteListComponent;
